'use client';
import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, usePathname } from 'next/navigation';
import { getUserContext } from '@/lib/userContext';
import { trackDiscoveryCallClick } from '@/lib/analytics';
import { supabase } from '@/lib/supabase';

type NavItem = {
  label: string;
  href: string;
  anchor?: string;
};

// All roles share the same nav links — Dashboard and Login are handled separately
const NAV_ITEMS: NavItem[] = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'How It Works', href: '/how-it-works-process-guide' },
  { label: 'Pricing', href: '/pricing-plans-subscription-details' },
  { label: 'Contact', href: '/contact' },
];

const getDashboardHref = (role: string): string => {
  if (['admin', 'ops_admin', 'super_admin'].includes(role)) return '/admin/dashboard';
  if (role === 'client') return '/client/dashboard';
  if (role === 'member' || role === 'team') return '/member/dashboard';
  return '/';
};

const getRoleLabel = (role: string): string => {
  switch (role) {
    case 'super_admin': return 'Super Admin';
    case 'ops_admin': return 'Ops Admin';
    case 'admin': return 'Admin';
    case 'member': return 'Member';
    case 'team': return 'Member';
    case 'client': return 'Client';
    default: return '';
  }
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [userContext, setUserContext] = useState<any>(null);
  const [contextLoading, setContextLoading] = useState(true);

  // Change Password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [cpNewPassword, setCpNewPassword] = useState('');
  const [cpConfirmPassword, setCpConfirmPassword] = useState('');
  const [cpSaving, setCpSaving] = useState(false);
  const [cpError, setCpError] = useState('');
  const [cpSuccess, setCpSuccess] = useState(false);

  const profileRef = useRef<HTMLDivElement>(null);
  const { user, userProfile } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const loadContext = async () => {
      setContextLoading(true);
      if (user) {
        const context = await getUserContext();
        setUserContext(context);
      } else {
        setUserContext(null);
      }
      setContextLoading(false);
    };
    loadContext();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setProfileOpen(false);
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const handleNavClick = (item: NavItem) => {
    if (item.anchor) {
      router.push(item.href);
      setTimeout(() => {
        const element = document.getElementById(item.anchor!);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setCpError('');
    if (!cpNewPassword || cpNewPassword.length < 8) {
      setCpError('Password must be at least 8 characters.');
      return;
    }
    if (cpNewPassword !== cpConfirmPassword) {
      setCpError('Passwords do not match.');
      return;
    }
    setCpSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: cpNewPassword });
      if (error) throw error;
      setCpSuccess(true);
      setTimeout(() => {
        setShowChangePassword(false);
        setCpNewPassword('');
        setCpConfirmPassword('');
        setCpSuccess(false);
      }, 1500);
    } catch (err: any) {
      setCpError(err?.message || 'Failed to update password.');
    } finally {
      setCpSaving(false);
    }
  };

  const isAuthenticated = userContext?.isAuthenticated || false;
  const isClient = userContext?.role === 'client';
  const fullName = userProfile?.full_name || userProfile?.name || userContext?.email || 'User';
  const initials = fullName?.charAt(0)?.toUpperCase();
  const roleLabel = getRoleLabel(userContext?.role || '');
  const dashboardHref = getDashboardHref(userContext?.role || '');

  const isActiveLink = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname?.startsWith(href);
  };

  const renderAccountDropdown = () => (
    <div className="relative" ref={profileRef}>
      <button
        onClick={() => setProfileOpen(!profileOpen)}
        className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-white/20 border border-white/30 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-semibold text-white">{initials}</span>
        </div>
        <div className="flex flex-col leading-tight text-left">
          <span className="text-sm font-semibold text-white truncate max-w-[120px]">{fullName}</span>
          {roleLabel && (
            <span className="text-xs text-purple-200 truncate max-w-[120px]">{roleLabel}</span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-purple-200 ml-0.5 transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {profileOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          {userContext?.email && (
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs text-gray-400 truncate">{userContext.email}</p>
            </div>
          )}

          {/* Dashboard — dynamic by role */}
          <Link
            href={dashboardHref}
            onClick={() => setProfileOpen(false)}
            className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Dashboard
          </Link>

          {/* Account — clients only */}
          {isClient && (
            <>
              <div className="border-t border-gray-100" />
              <button
                onClick={() => { setProfileOpen(false); router.push('/client-account-profile-management'); }}
                className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Account
              </button>
            </>
          )}

          {/* Change Password */}
          <div className="border-t border-gray-100" />
          <button
            onClick={() => { setProfileOpen(false); setShowChangePassword(true); }}
            className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Change Password
          </button>

          {/* Logout */}
          <div className="border-t border-gray-100" />
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <header className="w-full bg-[#2c0f3b] sticky top-0 z-50">
        <div className="w-full max-w-[1328px] mx-auto px-4 sm:px-6 lg:px-10 xl:px-14 py-6">
          <div className="flex flex-row justify-between items-center w-full h-full">
            {/* Logo */}
            <div className="flex items-center">
              <Link href="/">
                <Image
                  src="/images/img_purple_cow_logo_38b8c3_svg.svg"
                  alt="Purple Cow Logo"
                  width={274}
                  height={38}
                  className="w-[110px] h-auto sm:w-[160px] lg:w-[220px] cursor-pointer"
                />
              </Link>
            </div>

            {/* Hamburger (Mobile) */}
            <button
              className="block lg:hidden p-2"
              aria-label="Open menu"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Nav */}
            <nav
              className={`${
                menuOpen ? 'flex' : 'hidden'
              } lg:flex absolute lg:relative top-full lg:top-0 left-0 lg:left-auto w-full lg:w-auto bg-[#2c0f3b] lg:bg-transparent z-50 lg:z-auto flex-col lg:flex-row`}
            >
              <div className="flex flex-col lg:flex-row gap-4 sm:gap-5 lg:gap-6 xl:gap-[36px] justify-center items-center p-6 sm:p-8 lg:p-0">
                {contextLoading ? (
                  <div className="flex gap-8 items-center">
                    <div className="h-5 w-16 bg-white/20 rounded animate-pulse" />
                    <div className="h-5 w-16 bg-white/20 rounded animate-pulse" />
                    <div className="h-5 w-16 bg-white/20 rounded animate-pulse" />
                  </div>
                ) : (
                  <>
                    {/* Nav links — same for all roles */}
                    {NAV_ITEMS.map((item) =>
                      item.anchor ? (
                        <button
                          key={item.label}
                          onClick={() => handleNavClick(item)}
                          className="text-[14px] sm:text-[15px] font-poppins font-medium leading-[1.5] text-center lg:text-left text-text-white w-auto hover:text-primary-light transition-colors duration-200"
                        >
                          {item.label}
                        </button>
                      ) : (
                        <Link
                          key={item.label}
                          href={item.href}
                          className={`text-[14px] sm:text-[15px] font-poppins font-medium leading-[1.5] text-center lg:text-left text-text-white w-auto hover:text-primary-light transition-colors duration-200 ${
                            isActiveLink(item.href) ? 'underline decoration-2 underline-offset-4' : ''
                          }`}
                        >
                          {item.label}
                        </Link>
                      )
                    )}

                    {/* Book a discovery call */}
                    <a
                      href="https://calendly.com/john-smulo/purple-cow-unlimited-discovery-call"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        const segment = userContext?.role || 'unauthenticated';
                        trackDiscoveryCallClick(
                          segment === 'super_admin' ? 'super_admin' :
                          segment === 'ops_admin' || segment === 'admin' ? 'admin' :
                          segment === 'team' ? 'team' :
                          segment === 'client' ? 'client' : 'unauthenticated',
                          pathname || '/',
                          'header'
                        );
                      }}
                      className="px-5 py-2.5 bg-white text-[#8923b3] hover:bg-[#f8f6fa] font-poppins font-semibold text-[14px] rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Book a discovery call
                    </a>

                    {/* Right of book button: Login (unauthenticated) or Account dropdown (authenticated) */}
                    {isAuthenticated ? (
                      renderAccountDropdown()
                    ) : (
                      <Link
                        href="/login"
                        className={`text-[14px] sm:text-[15px] font-poppins font-medium leading-[1.5] text-center lg:text-left text-text-white w-auto hover:text-primary-light transition-colors duration-200 ${
                          isActiveLink('/login') ? 'underline decoration-2 underline-offset-4' : ''
                        }`}
                      >
                        Login
                      </Link>
                    )}
                  </>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900">Change Password</h3>
            </div>
            {cpSuccess ? (
              <div className="py-4 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-900">Password updated!</p>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={cpNewPassword}
                    onChange={(e) => setCpNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={cpConfirmPassword}
                    onChange={(e) => setCpConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                {cpError && <p className="text-xs text-red-500">{cpError}</p>}
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePassword(false);
                      setCpNewPassword('');
                      setCpConfirmPassword('');
                      setCpError('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={cpSaving}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    {cpSaving ? 'Saving...' : 'Update'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;